"use client";

import React from "react";
import { resolve_icon_layout_size_px } from "@/components/icons/icons-utils/icon-size-tokens";
import { resolve_icon_stroke_width } from "@/components/icons/icons-utils/icon-stroke-config";
import type { PlaceholderIconProps } from "@/components/icons/icons-utils/icon-props";

// MARK: create_placeholder_icon
/**
 * Fallback component when {@link get_icon_component} has no entry (unknown `ICONS` key).
 *
 * **Reuse scope:** Project-wide — `icon-component-registry` only.
 */
export function create_placeholder_icon(display_name: string): React.FC<PlaceholderIconProps> {
    const Placeholder = (props: PlaceholderIconProps) => {
        const {
            color = "neutral",
            colorIntensity = 500,
            size: size_prop,
            strokeWidth,
            style,
            className,
            width,
            height,
            ...rest
        } = props;

        const resolved_size = resolve_icon_layout_size_px(size_prop);
        const width_rem = `${resolved_size / 16}rem`;
        const height_rem = `${resolved_size / 16}rem`;
        const resolved_width = width ?? width_rem;
        const resolved_height = height ?? height_rem;
        const effective_stroke_width = resolve_icon_stroke_width(resolved_size, strokeWidth);

        const default_style: React.CSSProperties = {
            color: `var(--${String(color)}-${colorIntensity})`,
        };

        return (
            <svg
                viewBox="0 0 24 24"
                width={resolved_width}
                height={resolved_height}
                className={className}
                style={{ ...default_style, ...style }}
                strokeWidth={effective_stroke_width}
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                data-placeholder-icon={display_name}
                {...rest}
            >
                <rect
                    x="3"
                    y="3"
                    width="18"
                    height="18"
                    rx="2"
                    fill="currentColor"
                    opacity={0.12}
                    stroke="none"
                />
            </svg>
        );
    };

    Placeholder.displayName = `PlaceholderIcon(${display_name})`;
    return Placeholder;
}
